/**
 * 品牌配置 —— 运行时按访问域名自动切换
 *
 * 背景：
 *   这套站点同时挂在 fssy.ltd 和 acrdock.com 两个域名下（同一份 dist）。
 *   以前的做法是「改一次代码 → 打包一次 → 分别部署」，
 *   所以源码里到处是被注释掉的备用文案，例如：
 *     - public/index.html 里注释掉的另一条 <title>
 *     - Header.vue 里注释掉的 <div class="logo1">富升</div>
 *   现在改成运行时读 location.hostname 判断，一份 dist 同时适配两个域名，
 *   以后要改品牌文案只改本文件，不用再动组件、也不用重新打包两次。
 *
 * 用法：
 *   import BRAND from '../brand'        // 组件里直接用
 *   import { applyBrandToHead } from './brand'   // main.js 里调用，刷新 title/meta
 */

// 域名 → 品牌 key。写主域名即可，会自动匹配 www. 及任意子域名
var HOST_MAP = {
    fssy: ['fssy.ltd'],
    acrdock: ['acrdock.com']
};

// 匹配不到任何域名时的兜底品牌（本地 localhost 开发也走这里）
var DEFAULT_BRAND = 'acrdock';

var BRANDS = {
    fssy: {
        key: 'fssy',
        // 头部 logo 文字（对应 .logo1 / .logo2 两个滚动状态）
        logoText: 'FSSY',
        // 浏览器标签标题
        title: '富升实业-水上浮桥，塑料浮筒，海洋渔排，专业游艇码头，浮动码头，钢结构码头，铝合金码头，景观浮桥',
        keywords: '富升实业,FSSY,水上浮桥,游艇码头,浮动码头,游船码头,亲水平台,码头建造，浮筒平台',
        description: '富升实业专注于大型水上浮桥、水上码头、亲水平台、浮动码头、游艇码头、游船码头、景观浮桥、水上房屋等水上项目的策划、设计、建造、产品供应等一体化服务',
        // 页脚品牌行 / 联系页公司名
        brandCn: '富升实业（广州）有限公司',
        brandShort: '富升实业',
        companyCn: '富升实业（广州）有限公司',
        companyEn: 'Fusheng Industrial (Guangzhou) Co., Ltd.',
        copyright: '© 2021 富升实业（广州）有限公司 FSSY COPYRIGHT. 版权所有.',
        icp: '粤ICP备2021131958号',
        web: 'www.fssy.ltd',
        // 注意：fssy.ltd 目前也用 acrdock.com 的邮箱（真实存在的邮箱）。
        // 如果以后开通了 @fssy.ltd 邮箱，只改这两行即可，全站同步生效。
        email: 'yannickhe@acrdock.com',
        emails: 'yannickhe@acrdock.com，sales@acrdock.com',
        tel: '18925328565，+86 189 2532 8685'
    },
    acrdock: {
        key: 'acrdock',
        logoText: 'ACRDOCK',
        title: 'Acr浮筒-HDPE MODULAR PONTOON,ALUMINUM/STEEL PONTOON,JET SKI DOCK/BOAT DOCK',
        keywords: 'HDPE MODULAR PONTOON,ALUMINUM/STEEL PONTOON,JET SKI DOCK/BOAT DOCK,水上浮桥,游艇码头,浮动码头,游船码头,亲水平台,码头建造，浮筒平台',
        description: 'HDPE MODULAR PONTOON,ALUMINUM/STEEL PONTOON,JET SKI DOCK/BOAT DOCK,游艇码头专注于大型水上浮桥、水上码头、亲水平台、浮动码头、游艇码头、游船码头、景观浮桥、水上房屋等水上项目的策划、设计、建造、产品供应等一体化服务',
        brandCn: 'AcrDock 富升实业（广州）有限公司',
        brandShort: '富升实业',
        companyCn: '富升实业（广州）有限公司',
        companyEn: 'Guangzhou Acrdock Company Limited',
        copyright: '© 2021 富升实业（广州）有限公司 ACRDOCK COPYRIGHT. 版权所有.',
        icp: '粤ICP备2021131958号',
        web: 'www.acrdock.com',
        email: 'yannickhe@acrdock.com',
        emails: 'yannickhe@acrdock.com，sales@acrdock.com',
        tel: '18925328565，+86 189 2532 8685'
    }
};

function getHostname() {
    try {
        return (window.location.hostname || '').toLowerCase();
    } catch (e) {
        return '';
    }
}

function resolveBrandKey() {
    var host = getHostname();
    if (!host) return DEFAULT_BRAND;

    var keys = Object.keys(HOST_MAP);
    for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var domains = HOST_MAP[key] || [];
        for (var j = 0; j < domains.length; j++) {
            var d = String(domains[j]).toLowerCase();
            // 命中主域名本身，或它的任意子域名（www.xxx / m.xxx ...）
            if (host === d || host.indexOf('.' + d) === host.length - d.length - 1) {
                return key;
            }
        }
    }
    return DEFAULT_BRAND;
}

function resolveBrand() {
    return BRANDS[resolveBrandKey()] || BRANDS[DEFAULT_BRAND];
}

var BRAND = resolveBrand();

/**
 * 把品牌文案写进 <head>（title / keywords / description）。
 * index.html 里只能写死一份，所以真正的按域名区分必须靠这里在运行时覆盖。
 */
function applyBrandToHead(brand) {
    var b = brand || BRAND;
    if (typeof document === 'undefined') return;

    if (b.title) document.title = b.title;

    var kw = document.querySelector('meta[name="keywords"]');
    if (kw) {
        kw.setAttribute('content', b.keywords);
    } else if (b.keywords) {
        var mk = document.createElement('meta');
        mk.setAttribute('name', 'keywords');
        mk.setAttribute('content', b.keywords);
        document.head.appendChild(mk);
    }

    var ds = document.querySelector('meta[name="description"]');
    if (ds) {
        ds.setAttribute('content', b.description);
    } else if (b.description) {
        var md = document.createElement('meta');
        md.setAttribute('name', 'description');
        md.setAttribute('content', b.description);
        document.head.appendChild(md);
    }
}

export default BRAND;
export { BRAND, BRANDS, HOST_MAP, DEFAULT_BRAND, resolveBrand, resolveBrandKey, applyBrandToHead };
